/**
 * DictSection — секция панели Справочников («Расходники» | «Ресурсы»),
 * эталон screen-21 H:259/263: хедер-ряд (иконка, заголовок с (N) ПО
 * ОТФИЛЬТРОВАННОМУ списку — Д14, шеврон, «?», два fchip-фильтра
 * Филиал/Квест — Dropdown single с «Все» и каскадом, «+» icon-only — Д12),
 * форма создания cform (Название* + unitCtl у расходников), строки mrow
 * (бейдж единицы/филиала, «в K»/«не используется», ✕, SwipeDup-дубликат);
 * тап по строке — аккордеон карточки (renderCard хоста: DictConsCard /
 * DictResCard). Тап по хедеру сворачивает секцию, клики по его кнопкам —
 * нет (J:397-398); у свёрнутой секции хедер полный, «+» виден всегда.
 */
import { useState, type MouseEvent, type ReactNode, type RefObject } from "react";
import type { Branch, Quest } from "../../api/types";
import type { DictConsumable, DictResource } from "../../api/dictTypes";
import { Dropdown, type DropdownId } from "../../components/shared";
import { fltActive, type DictFlt } from "../../hooks/dictFilters";
import type { DictKind } from "../../hooks/useDictsAdmin";
import { CheckIco, CrossIco, PlusIco } from "./questIcons";
import { DictUnitCtl } from "./DictUnitCtl";
import { SwipeDup } from "./SwipeDup";
import cfg from "./config.module.css";
import pcss from "./prices.module.css";
import qcss from "./quests.module.css";
import scss from "./services.module.css";
import css from "./dicts.module.css";

/** Стейт формы создания; id — client-id (один на сессию формы). */
export interface DictFormSt { id: string; n: string; u: string; custom: boolean; focus: boolean }
export type DictRow = DictConsumable | DictResource;

/* Иконки хедеров эталона (H:259 коробка pink / H:263 ключ cyan, 13px). */
const BoxIco = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 8l-9-5-9 5v8l9 5 9-5z" /><path d="M3 8l9 5 9-5M12 13v8" />
  </svg>
);
const WrenchIco = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20.5 6.5a5 5 0 0 1-6.6 4.8L7 18.2V21H4v-3l6.7-6.9A5 5 0 0 1 17.5 3l-2.8 2.8 1 2.5 2.5 1z" />
  </svg>
);

export interface DictSectionProps {
  kind: DictKind;
  /** Уже отфильтрованные и отсортированные строки (счётчик заголовка = length). */
  rows: DictRow[];
  open: boolean;
  onToggle: () => void;
  flt: DictFlt;
  /** Хост: setFlt + secOpen=true (выбор фильтра авторазворачивает секцию). */
  onFlt: (f: DictFlt) => void;
  branches: Branch[];
  quests: Quest[];
  onTip: (anchor: HTMLElement) => void;
  formSt: DictFormSt | null;
  formRef: RefObject<HTMLDivElement | null>;
  busy: boolean;
  onPlus: () => void;
  onForm: (st: DictFormSt) => void;
  onSubmit: () => void;
  onCancelForm: () => void;
  flash: { key: string; n: number } | null;
  editId: string | null;
  /** Раскрытая карточка позиции (DictConsCard / DictResCard — DICT-3). */
  renderCard: (row: DictRow) => ReactNode;
  onOpenRow: (row: DictRow) => void;
  onCloseRow: () => void;
  onDup: (row: DictRow) => void;
  onDel: (row: DictRow) => void;
}

export function DictSection(p: DictSectionProps) {
  const [dd, setDd] = useState<{ which: "br" | "q"; anchor: HTMLElement } | null>(null);
  const c = p.kind === "c";
  const st = p.formSt;
  const brName = (id: string | null | undefined): string | null =>
    (id ? p.branches.find((b) => b.id === id)?.name ?? id : null);
  const qName = (id: string): string => p.quests.find((q) => q.id === id)?.name ?? id;

  const headTap = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target instanceof Element && e.target.closest("button")) return;
    p.onToggle();
  };
  /* Каскад эталона J:360-372: список квестов сужен филиалом; смена филиала
     сбрасывает несовместимый квест. */
  const ddItems = dd?.which === "br"
    ? [{ id: "__all", label: "Все" }, ...p.branches.map((b) => ({ id: b.id, label: b.name }))]
    : [{ id: "__all", label: "Все" },
      ...(p.flt.br ? p.quests.filter((q) => q.branch_id === p.flt.br) : p.quests)
        .map((q) => ({ id: q.id, label: q.name }))];
  const ddCommit = (ids: DropdownId[]) => {
    const id = ids[0];
    if (id == null || !dd) return;
    const v = id === "__all" ? null : String(id);
    if (dd.which === "br") {
      const qBr = p.flt.q ? p.quests.find((q) => q.id === p.flt.q)?.branch_id : undefined;
      p.onFlt({ br: v, q: p.flt.q && v && qBr !== v ? null : p.flt.q });
    } else p.onFlt({ br: p.flt.br, q: v });
  };
  const valid = !!st && !!st.n.trim() && (!c || !!st.u.trim());

  return (
    <>
      <div className={`${qcss.s17head} ${css.headTap}`} style={c ? { marginTop: 5 } : undefined} onClick={headTap}>
        <span className={c ? css.ipk : css.icy}>{c ? <BoxIco /> : <WrenchIco />}</span>
        <h2 className={`${cfg.secT} ${c ? "" : cfg.cy}`}>{c ? "Расходники" : "Ресурсы"} ({p.rows.length})</h2>
        <span className={`${css.chev} ${c ? css.pk : css.cy} ${p.open ? css.up : ""}`}>▾</span>
        <button type="button" className={pcss.qmark} data-qmark="" aria-label="Справка"
          onClick={(e) => { e.stopPropagation(); p.onTip(e.currentTarget); }}>
          <span>?</span>
        </button>
        <button type="button" className={`${css.fchip} ${p.flt.br ? css.on : ""}`}
          onClick={(e) => setDd({ which: "br", anchor: e.currentTarget })}>
          <span className={css.fv}>{p.flt.br ? brName(p.flt.br) : "Филиал"}</span>
          <span className={css.chev}>▾</span>
        </button>
        <button type="button" className={`${css.fchip} ${p.flt.q ? css.on : ""}`}
          onClick={(e) => setDd({ which: "q", anchor: e.currentTarget })}>
          <span className={css.fv}>{p.flt.q ? qName(p.flt.q) : "Квест"}</span>
          <span className={css.chev}>▾</span>
        </button>
        {p.open && st ? null : (
          <button type="button" className={`${qcss.abtn} ${qcss.cf} ${css.solo}`} title="Добавить"
            onClick={(e) => { e.stopPropagation(); p.onPlus(); }}>
            <PlusIco w={12} />
          </button>
        )}
      </div>
      <Dropdown
        open={!!dd} anchor={dd?.anchor ?? null} multi={false}
        selectedIds={[(dd?.which === "br" ? p.flt.br : p.flt.q) ?? "__all"]}
        items={ddItems} onClose={() => setDd(null)} onCommit={ddCommit}
      />
      {p.open && st ? (
        <div className={qcss.cform} ref={p.formRef}>
          <div className={cfg.secRow}>
            <h3 className={`${cfg.secT} ${qcss.cformT}`}>{c ? "Новый расходник" : "Новый ресурс"}</h3>
          </div>
          <div className={qcss.gap2}>
            <span className={cfg.klbl}>Название *</span>
            <input
              className={qcss.txt} value={st.n} autoFocus={st.focus}
              placeholder={c ? "Например, Стикеры" : "Например, Проектор №2"}
              onChange={(e) => p.onForm({ ...st, n: e.target.value })}
            />
          </div>
          {c ? (
            <div className={scss.erow}>
              <span className={`${cfg.klbl} ${scss.klblFlex}`}>Единица</span>
              <DictUnitCtl u={st.u} custom={st.custom} onChange={(u, custom) => p.onForm({ ...st, u, custom })} />
            </div>
          ) : null}
          <div className={qcss.apair}>
            <button type="button" className={`${qcss.abtn} ${qcss.cf}`} disabled={p.busy || !valid} onClick={p.onSubmit}>
              <CheckIco />{p.busy ? "Создаём…" : "Создать"}
            </button>
            <button type="button" className={`${qcss.abtn} ${qcss.cl}`} onClick={p.onCancelForm}>Отмена</button>
          </div>
        </div>
      ) : null}
      {p.open ? p.rows.map((row) => {
        const f = p.flash?.key === `${p.kind}:${row.id}` ? p.flash : null;
        const editing = p.editId === row.id;
        const badge = c
          ? (row as DictConsumable).unit || "шт"
          : brName((row as DictResource).branch_id);
        return (
          <article key={f ? `${row.id}:${f.n}` : row.id}
            className={`${scss.mcard} ${editing ? scss.editing : ""} ${f ? cfg.flash : ""}`}>
            {editing ? (
              p.renderCard(row)
            ) : (
              <SwipeDup disabled={false} onDup={() => p.onDup(row)} className={scss.mrow} onClick={() => p.onOpenRow(row)}>
                <span className={scss.mn}>{row.name}</span>
                {badge ? <span className={scss.shch}>{badge}</span> : null}
                {row.use_count > 0 ? (
                  <span className={css.use}>в <span className={`${qcss.num} ${css.unum}`}>{row.use_count}</span></span>
                ) : (
                  <span className={`${css.use} ${css.dim}`}>не используется</span>
                )}
                <button type="button" className={qcss.tx} title="Удалить"
                  onClick={(e) => { e.stopPropagation(); p.onDel(row); }}>
                  <CrossIco />
                </button>
              </SwipeDup>
            )}
          </article>
        );
      }) : null}
      {p.open && !p.rows.length ? (
        <div className={cfg.teaEmpty}>
          {fltActive(p.flt) ? (
            <>
              Ничего не найдено по фильтру{" "}
              <button type="button" className={css.fclear} onClick={() => p.onFlt({ br: null, q: null })}>
                Сбросить
              </button>
            </>
          ) : (c ? "Справочник пуст — добавьте первый расходник." : "Справочник пуст — добавьте первый ресурс.")}
        </div>
      ) : null}
    </>
  );
}
